import { HTMLAttributes } from 'react';
import { DragAndDropState } from '../DragAndDrop';
import { FiltersMap } from '@wix/bex-core';
import { action } from 'mobx';

export interface DndTestEventHandlersParams<T, F extends FiltersMap> {
  id: string | number;
  disabled?: {
    draggable?: boolean;
    droppable?: boolean;
  };
  state: DragAndDropState<T, F>;
}

export const sortableItemTestEvents = <T, F extends FiltersMap>({
  id,
  disabled,
  state,
}: DndTestEventHandlersParams<T, F>): Partial<HTMLAttributes<HTMLElement>> => {
  return {
    onPointerDown: () => {
      if (disabled?.draggable) {
        return;
      }
      state.onDragStart({ active: { id } });
    },
    onPointerUp: () => {
      if (disabled?.droppable || state.activeId == null) {
        return;
      }
      const isCancelled = state.beforeDrop({
        active: {
          id: state.activeId,
        },
        over: {
          id,
        },
      });
      if (isCancelled) {
        return;
      }
      state.onDragEnd({
        over: {
          id,
        },
      });
    },
    onKeyDown: (e) => {
      const keyboardCodes = state.keyboardCodes;

      if (
        state.status === 'start' &&
        keyboardCodes.move.includes(e.nativeEvent.code)
      ) {
        state.onDragOver({
          over: {
            id,
          },
        });
        return;
      }
    },
  };
};

export const dragHandleTestEvents = <T, F extends FiltersMap>({
  id,
  state,
}: DndTestEventHandlersParams<T, F>): Partial<HTMLAttributes<HTMLElement>> => {
  return {
    onKeyDown: action((e) => {
      state.handleEvents?.onKeyDown?.(e);

      const keyboardCodes = state.keyboardCodes;

      if (state.status === 'start' || state.status === 'over') {
        if (keyboardCodes.end.includes(e.nativeEvent.code)) {
          if (state.activeId == null) {
            return;
          }
          state.beforeDrop({
            active: { id: state.activeId },
            over: { id },
          });
          state.onDragEnd({ over: { id } });
          return;
        }

        if (keyboardCodes.cancel.includes(e.nativeEvent.code)) {
          state.onDragCancel();
          return;
        }
      }

      if (keyboardCodes.start.includes(e.nativeEvent.code)) {
        state.onDragStart({ active: { id } });
        return;
      }
    }),
  };
};
